import type { Question } from "../data/types";
import type { BankStats } from "../data/questions";

/** Gốc API. Mặc định "/api" — cùng domain với web (nginx/vite proxy chuyển tới backend). */
export const API_URL = ((import.meta.env.VITE_API_URL as string | undefined) ?? "/api").replace(/\/$/, "");
export const TOKEN_KEY = "claude-cert-prep:token:v1";

export interface ApiUser { id: string; email: string; name: string | null; avatar_url: string | null; created_at: string; isAdmin?: boolean }
/** Câu hỏi admin thêm trên giao diện — server kiểm tra lại theo phân loại chính thức của đề. */
export interface AdminQuestionInput {
  exam: string;
  domain: string;
  scenario?: string;
  objective?: string;
  difficulty: 1 | 2 | 3;
  answer: number[];
  isPublic?: boolean;
  en: { prompt: string; code?: string; options: string[]; explanation: string };
  vi: { prompt: string; options: string[]; explanation: string };
  notes?: { vi: string[]; en: string[] };
  takeaway?: { vi: string; en: string };
  sources?: { title: string; url: string }[];
}
export interface SandboxRunResult { output: string; exitCode: number; truncated: boolean }

export interface AdminOverview {
  users: { total: number; new_30d: number; logged_in_30d: number };
  dailyUsers: { day: string; count: number }[];
  dailyProgress: { day: string; count: number }[];
  progress: { learners: number; attempts: number; lab_records: number; synced_30d: number };
  questions: { exam: string; active: number; public: number; retired: number }[];
  activeQuestions: number;
  feedback: { status: string; count: number }[];
  unresolvedFeedback: number;
  recentFeedback: AdminFeedback[];
}

export interface AdminFeedback {
  id: number; kind: string; message: string; question_id: string | null; page?: string | null;
  status: string; created_at: string; user_label?: string | null; email?: string | null;
  lang?: string | null; user_name?: string | null; user_email?: string | null;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t: string | null): void {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* bỏ qua */ }
}

async function call<T>(path: string, init: RequestInit = {}, auth = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  const token = auth ? getToken() : null;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? `http-${res.status}`);
  return data;
}

export const api = {
  loginGoogle: (credential: string) => call<{ token: string; user: ApiUser }>("/auth/google", { method: "POST", body: JSON.stringify({ credential }) }, false),
  loginDev: (email: string) => call<{ token: string; user: ApiUser }>("/auth/dev", { method: "POST", body: JSON.stringify({ email }) }, false),
  me: () => call<{ user: ApiUser }>("/me"),
  deleteMe: () => call<{ ok: true }>("/me", { method: "DELETE" }),
  questions: () => call<{ questions: Question[] }>("/questions"),
  publicQuestions: () => call<{ questions: Question[]; stats: BankStats }>("/questions/public", {}, false),
  getProgress: () => call<{ progress: unknown | null }>("/progress"),
  putProgress: (progress: unknown) => call<{ ok: true }>("/progress", { method: "PUT", body: JSON.stringify({ progress }) }),
  feedback: (body: unknown) => call<{ ok: true }>("/feedback", { method: "POST", body: JSON.stringify(body) }),
  labRunnerStatus: () => call<{ available: boolean }>("/labs/runner-status"),
  runLabCode: (language: "python" | "javascript", code: string) => call<SandboxRunResult>("/labs/run", { method: "POST", body: JSON.stringify({ language, code }) }),
  adminOverview: () => call<AdminOverview>("/admin/overview"),
  adminUsers: (page: number, q: string) => call<{ users: { id: string; email: string; name: string | null; avatar_url: string | null; created_at: string; last_login_at: string; progress_updated_at: string | null; attempts: number }[]; total: number; page: number; limit: number }>(`/admin/users?page=${page}&q=${encodeURIComponent(q)}`),
  adminDeleteUser: (id: string) => call<{ ok: true }>(`/admin/users/${encodeURIComponent(id)}`, { method: "DELETE" }),
  adminFeedback: (page: number, status: string, kind: string, q: string) => call<{ feedback: AdminFeedback[]; total: number; page: number; limit: number; statuses: string[] }>(`/admin/feedback?page=${page}&status=${encodeURIComponent(status)}&kind=${encodeURIComponent(kind)}&q=${encodeURIComponent(q)}`),
  adminSetFeedbackStatus: (id: number, status: string) => call<{ feedback: { id: number; status: string } }>(`/admin/feedback/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  adminQuestions: (page: number, exam: string, q: string) => call<{ questions: { id: string; exam: string; domain: string; is_public: boolean; retired: boolean; source: string; updated_at: string; prompt: string | null; difficulty: string | null; options: string[] | null; answer: number[] | null }[]; total: number; page: number; limit: number; counts: { exam: string; active: number; public: number; retired: number }[] }>(`/admin/questions?page=${page}&exam=${encodeURIComponent(exam)}&q=${encodeURIComponent(q)}`),
  adminCreateQuestion: (body: AdminQuestionInput) => call<{ question: { id: string; exam: string; domain: string; is_public: boolean } }>("/admin/questions", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateQuestion: (id: string, update: { isPublic?: boolean; retired?: boolean }) => call<{ question: { id: string; is_public: boolean; retired: boolean } }>(`/admin/questions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(update) }),
};
